import {
  ListItemButton,
  useTheme,
  useMediaQuery,
  Box,
  Theme,
} from "@mui/material";
import React from "react";

import { SidebarMenuItemCollapseIcon } from "./SidebarMenuItemCollapseIcon";
import { SidebarMenuItemIcon } from "./SidebarMenuItemIcon";
import { SidebarMenuItemText } from "./SidebarMenuItemText";
import type { SidebarMenuItemRowProps } from "./types";
import { sidebarTheme } from "@/styles/sidebarTheme";

// Clean modern styling - only colors, no borders/shadows/animations
const getCleanStyling = (_theme: Theme, isSelected: boolean, level: number) => {
  const isTopLevel = level === 0;

  return {
    backgroundColor: isSelected
      ? sidebarTheme.menu.selected.background
      : "transparent",
    hoverBackground: isTopLevel
      ? sidebarTheme.menu.hover.backgroundTopLevel
      : sidebarTheme.menu.hover.backgroundSubmenu,
    textColor: isSelected
      ? sidebarTheme.menu.selected.text
      : sidebarTheme.menu.normal.text,
    fontWeight: isSelected ? 600 : isTopLevel ? 500 : 400,
  };
};
export const SidebarMenuItemButton: React.FC<SidebarMenuItemRowProps> = (
  props
) => {
  const {
    item,
    openItems,
    setOpenItems,
    handleLogout,
    navigate,
    level = 0,
    selectedItem,
    setSelectedItem,
    onMenuItemClick,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hasChildren = !!item.items?.length;
  const isLogout = item.text.toLowerCase() === "esci";
  const isSelected = selectedItem === item.text;
  const isExpanded = !!openItems[item.text];
  const itemPadding = theme.spacing(1.5 + level * 2);
  const isTopLevel = level === 0;

  const styling = getCleanStyling(theme, isSelected, level);

  const handleClick = React.useCallback(() => {
    if (hasChildren)
      setOpenItems((prev) => ({ ...prev, [item.text]: !prev[item.text] }));
    else if (isLogout) {
      handleLogout();
      if (onMenuItemClick && isMobile) onMenuItemClick();
    } else if (item.path) {
      navigate(item.path);
      if (onMenuItemClick && isMobile) onMenuItemClick();
    }
    if (setSelectedItem) setSelectedItem(item.text);
  }, [
    hasChildren,
    setOpenItems,
    item,
    isLogout,
    handleLogout,
    navigate,
    setSelectedItem,
    isMobile,
    onMenuItemClick,
  ]);

  return (
    <ListItemButton
      onClick={handleClick}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        pl: itemPadding,
        pr: isTopLevel ? 1.5 : 2,
        py: isTopLevel ? 1.2 : 0.8,
        minHeight: isTopLevel ? 48 : 36,
        mb: isTopLevel ? 0.5 : 0.2,
        mx: isTopLevel ? 1 : 0.5,
        ml: isTopLevel ? 1 : itemPadding,
        borderRadius: 1.5,
        backgroundColor: styling.backgroundColor,
        fontWeight: styling.fontWeight,
        fontSize: isTopLevel ? "0.95rem" : "0.875rem",

        "&:hover": {
          backgroundColor: styling.hoverBackground,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
        <SidebarMenuItemIcon
          Icon={item.icon}
          isSelected={isSelected}
          level={level}
        />
        <SidebarMenuItemText
          text={item.text}
          isSelected={isSelected}
          level={level}
        />
      </Box>
      {hasChildren && <SidebarMenuItemCollapseIcon isExpanded={isExpanded} />}
    </ListItemButton>
  );
};
